import { useState } from "react";
import EmbedFlow from "@formsort/react-embed";
import { createFileRoute, redirect, useNavigate, useSearch } from "@tanstack/react-router";
import { v7 as uuidv7 } from "uuid";

import { ScreenSpinner } from "@/components";
import { analytics } from "@/config/analytics";
import { POST_PURCHASE_FINAL_STEPS } from "@/domains/surveys/models/constants";
import { postPurchaseSurveySearchSchema } from "@/domains/surveys/models/schemas";
import { usePostPurchaseSurveyParams } from "@/domains/surveys/queries/use-post-purchase-survey-params";
import { env } from "@/env";

const PostPurchaseSurvey = () => {
  // eslint-disable-next-line react/hook-use-state
  const [responderUuid] = useState(() => {
    return uuidv7();
  });
  const navigate = useNavigate();
  const { effectiveness, order_id: orderId } = useSearch({
    from: "/_guest/post-purchase/survey/",
  });
  const { surveyParams } = usePostPurchaseSurveyParams(orderId);

  const handleFlowFinalized = (stepId?: string) => {
    analytics.track("flow_finalized", {
      flow_label: env.VITE_FORMSORT_POST_PURCHASE_SURVEY_FLOW_LABEL,
      responder_uuid: responderUuid,
    });

    if (stepId === POST_PURCHASE_FINAL_STEPS.SHOW_RECOMMENDATIONS) {
      return navigate({
        to: "/post-purchase/recommendations",
        search: { flow_uuid: responderUuid, order_id: orderId },
      });
    }

    if (stepId === POST_PURCHASE_FINAL_STEPS.SHOW_SUBSCRIPTION) {
      return navigate({
        to: "/post-purchase/subscribe",
        search: { order_id: orderId },
      });
    }

    navigate({ to: "/" });
  };

  if (!surveyParams) {
    return <ScreenSpinner />;
  }

  return (
    <div className="flex-1 bg-canvas">
      <EmbedFlow
        clientLabel={env.VITE_FORMSORT_POST_PURCHASE_SURVEY_CLIENT_LABEL}
        embedConfig={{ autoHeight: true, style: { width: "100%" } }}
        flowLabel={env.VITE_FORMSORT_POST_PURCHASE_SURVEY_FLOW_LABEL}
        formsortEnv={env.VITE_FORMSORT_POST_PURCHASE_SURVEY_ENV}
        onFlowFinalized={({ stepId }) => {
          handleFlowFinalized(stepId);
        }}
        onFlowLoaded={() => {
          analytics.track("flow_loaded", {
            flow_label: env.VITE_FORMSORT_POST_PURCHASE_SURVEY_FLOW_LABEL,
            responder_uuid: responderUuid,
          });
        }}
        onStepCompleted={({ stepId, stepIndex }) => {
          if (!stepId || stepIndex === undefined) {
            return;
          }
          analytics.track("step_completed", {
            flow_label: env.VITE_FORMSORT_POST_PURCHASE_SURVEY_FLOW_LABEL,
            responder_uuid: responderUuid,
            step_id: stepId,
            step_index: stepIndex,
          });
        }}
        queryParams={[...surveyParams.rawParams, ["Effectiveness", effectiveness]]}
        responderUuid={responderUuid}
        variantLabel={env.VITE_FORMSORT_POST_PURCHASE_SURVEY_VARIANT_LABEL}
      />
    </div>
  );
};

export const Route = createFileRoute("/_guest/post-purchase/survey/")({
  component: PostPurchaseSurvey,
  validateSearch: postPurchaseSurveySearchSchema,
  onError: (error) => {
    if (error.routerCode === "VALIDATE_SEARCH") {
      throw redirect({ to: "/" });
    }
  },
});
